import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { EmployeeItem } from "../types/employee";
import api from "../lib/axios";


//--------------------------------------------------
// THUNKS
//--------------------------------------------------

// GET all employees
export const getEmployees = createAsyncThunk<EmployeeItem[]>(
  "employees/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(`/employee/all`);
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employees");
    }
  }
);

// CREATE employee
export const createEmployee = createAsyncThunk<EmployeeItem, any>(
  "employees/create",
  async (payload, { rejectWithValue }) => {
    try {
      // console.log("Creation payload: ", payload);
      const res = await api.post(`/employee/add`, payload);
      return res.data.data; // backend returns created employee
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to create employee");
    }
  }
);

// UPDATE employee
export const updateEmployee = createAsyncThunk<EmployeeItem, { id: string; data: any }>(
  "employees/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      // console.log("Thunk data: ", data);
      // console.log("Thunk id: ", id);

      const res = await api.put(`/employee/update/${id}`, data);
      return res.data.data; // returns the updated employee object
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update employee");
    }
  }
);

// SOFT DELETE employees
export const deleteEmployees = createAsyncThunk(
  "employees/deleteEmployees",
  async (userIds: string[], { rejectWithValue }) => {
    try {
      const res = await api.put("/employee/soft-delete", { userIds });
      return res.data; // return message + ids of deleted users
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete employees"
      );
    }
  }
);


//--------------------------------------------------
// SLICE
//--------------------------------------------------

interface EmployeesState {
  employees: EmployeeItem[];
  loading: boolean;
  error: string | null;
  success: boolean;
  adding: boolean;
  updating: boolean;
}

const initialState: EmployeesState = {
  employees: [],
  loading: false,
  error: null,
  success: false,
  adding: false,
  updating: false,
};

const employeesSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    resetEmployeeState: (state) => {
      state.error = null;
      state.success = false;
    },
    optimisticAddEmployee: (state, action: PayloadAction<EmployeeItem>) => {
      state.adding = false;
      state.employees.push(action.payload);
    },
    rollbackAddEmployee: (state, action: PayloadAction<string>) => {
      state.adding = false;
      state.employees = state.employees.filter(emp => emp.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    // GET ALL
    builder.addCase(getEmployees.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.adding = false;
    });
    builder.addCase(getEmployees.fulfilled, (state, action: PayloadAction<EmployeeItem[]>) => {
      state.loading = false;
      console.log("Payload: ", action.payload);
      state.employees = action.payload ?? [];
      state.adding = false;
      state.success = true;
    });
    builder.addCase(getEmployees.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.adding = false;
    });

    // CREATE EMPLOYEE
    builder.addCase(createEmployee.pending, (state) => {
      state.adding = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createEmployee.fulfilled, (state, action: PayloadAction<any>) => {
      state.adding = false;
      state.success = true;
      console.log("Payload: ", action.payload);
      state.employees.unshift(action.payload);
    });
    builder.addCase(createEmployee.rejected, (state) => {
      state.adding = false;
      state.success = false;
    });
    builder.addCase(updateEmployee.pending, (state) => {
      state.updating = true;
    });

    builder.addCase(updateEmployee.fulfilled, (state, action: PayloadAction<EmployeeItem>) => {
      state.updating = false;
      state.success = true;

      // Replace the old employee with the updated one
      const idx = state.employees.findIndex((e) => e.id === action.payload.id);
      if (idx !== -1) state.employees[idx] = action.payload;
    })

    builder.addCase(updateEmployee.rejected, (state) => {
      state.updating = false;
    })

     .addCase(deleteEmployees.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteEmployees.fulfilled, (state, action) => {
        state.employees = state.employees.filter(
          (e) => !action.payload.data.includes(e.id)
        );
      })
      .addCase(deleteEmployees.rejected, (state, action) => {
        state.error = action.payload as string;
      });

  }
});

export const { 
  resetEmployeeState, 
  optimisticAddEmployee, 
  rollbackAddEmployee 
} = employeesSlice.actions;
export const employeeReducer = employeesSlice.reducer;
